import { styled } from "goober";
import { makeUrl, routes, useRouteMatch } from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { MainScrollContainer } from "../MainScrollContainer";
import { Text } from "../Text";

const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderInner = styled("div")`
  display: flex;
  align-items: center;
`;

const Tabs = styled("div")`
  display: flex;
  gap: 8px;
  padding: 12px;
  overflow: hidden;
`;

const Tab = styled(Link)`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  border: none;
  text-decoration: none;
  outline: none;
  background: none;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 24px;
  cursor: pointer;
  color: var(--text-secondary);

  &.active {
    color: var(--text-primary);
    background: var(--alpha4);
  }

  &:hover {
    background: linear-gradient(var(--alpha4)), var(--surface-primary);
    color: var(--text-primary);
  }
`;

const TABS = [
  {
    route: routes.monitoring.workflows,
    label: "Workflows",
    icon: "product-workflows",
  },
  {
    route: routes.monitoring.connectors,
    label: "Connectors",
    icon: "api-key-custom",
  },
  { route: routes.monitoring.access, label: "Access map", icon: "orbit" },
  { route: routes.monitoring.value, label: "Savings", icon: "wallet" },
  { route: routes.monitoring.ai, label: "AI", icon: "brain" },
] as const;

type Props = {
  children: React.ReactNode;
  headerItem?: React.ReactNode;
};

export function MonitoringLayout({ children, headerItem }: Props) {
  return (
    <Backdrop permanentNavigation>
      <Container>
        <div>
          <HeaderInner>
            <Tabs>
              {TABS.map(({ route, label, icon }) => (
                <MonitoringTab
                  key={label}
                  route={route}
                  label={label}
                  icon={icon}
                />
              ))}
            </Tabs>
            {headerItem}
          </HeaderInner>
        </div>
        <MainScrollContainer>{children}</MainScrollContainer>
      </Container>
    </Backdrop>
  );
}

// scope rides the parent route, so makeUrl carries it across tab navigation.
function MonitoringTab({
  route,
  label,
  icon,
}: {
  route: (typeof TABS)[number]["route"];
  label: string;
  icon: (typeof TABS)[number]["icon"];
}) {
  const [active] = useRouteMatch(route);

  return (
    <Tab to={makeUrl(route)} className={active ? "active" : ""}>
      <Icon name={icon} size={16} />
      <Text variant="text-small-md">{label}</Text>
    </Tab>
  );
}
